import { useState, useRef } from "react";
import axios from "axios";
import moment from "moment";
import Cookies from "js-cookie";
import fetchData from "./APIBaseMethod";
import dateUtils from "../helper/dateFormater";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const useCaseAPIDetails = () => {
  const [cases, setCases] = useState(null);
  const [appearingList, setAppearingList] = useState([]);
  const isSubmitting = useRef(false);
  const [loading, setLoading] = useState(false);

  // Fetch case details
  const getCaseDetails = async (id, type) => {
    const data = await fetchData(`/api/v1/case-details/${id}/get`);
    if (!data) return null;

    if (type === "Edit") {
      data.prevDate = data.nextDate;
      data.nextDate = "";
    }

    const updatedCaseData = {
      ...data,
      prevDate: dateUtils.formatDateForDisplay(data.prevDate),
      nextDate: dateUtils.formatDateForDisplay(data.nextDate),
    };

    setCases(updatedCaseData);
    return updatedCaseData;
  };

  // Fetch appearing list
  const getAppearingLists = async () => {
    const data = await fetchData("/api/v1/config/appearinglist");
    if (data) setAppearingList(data);
    return data || [];
  };

  // Submit case details (create/update)
  const submitCaseDetails = async (id, caseData, onSuccess, onError) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setLoading(true);

    const formattedData = {
      ...caseData,
      prevDate: dateUtils.formatDateForInput(caseData.prevDate),
      nextDate: dateUtils.formatDateForInput(caseData.nextDate),
    };

    try {
      const url = id
        ? `/api/v1/case-details/${id}/update`
        : "/api/v1/case-details/create";

      const token = Cookies.get("auth_token");
      const response = await axios({
        method: id ? "put" : "post",
        url: `${API_BASE_URL}${url}`,
        data: formattedData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      response.data.status === "Success"
        ? onSuccess(response.data)
        : onError("Failed to process request.");
    } catch (error) {
      const errorMessage =
        error.response?.status === 400 && error.response.data?.errors?.fileNo
          ? "File number already taken."
          : "An error occurred while processing the case.";
      onError(errorMessage);
    } finally {
      isSubmitting.current = false;
      setLoading(false);
    }
  };

  // Fetch case list
  const fetchCaseListData = async (filters, caseStatus, paginationModel) => {
    const params = {
      start_date: filters.startDate
        ? moment(filters.startDate).format("YYYY-MM-DD")
        : undefined,
      end_date: filters.endDate
        ? moment(filters.endDate).format("YYYY-MM-DD")
        : undefined,
      court_id: filters.courtFilter,
      court_place: filters.courtPlaceFilter,
      case_status: JSON.stringify(caseStatus),
      page: paginationModel.page + 1, // Convert to one-based index
      per_page: paginationModel.pageSize,
      search: filters.searchFilter,
    };

    const data = await fetchData("/api/v1/case-details/list", params);
    if (data) {
      return data;
    } else {
      return null;
    }
  };

  const deleteData = async (id) => {
    try {
      const token = Cookies.get("auth_token");
      const response = await axios.delete(
        `${API_BASE_URL}/api/v1/case-details/${id}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.status === "Success";
    } catch (error) {
      console.error(`Error deleting ${id}:`, error);
      return false;
    }
  };

  return {
    cases,
    getCaseDetails,
    getAppearingLists,
    appearingList,
    submitCaseDetails,
    loading,
    fetchCaseListData,
    deleteData,
  };
};

export default useCaseAPIDetails;
